// Pantalla minimalista para grabar el audio del modo Diálogo del plugin. El
// plugin la abre vía openExternal: /grabar-dialogo?project_id=... El usuario
// graba (UXP no puede acceder al micrófono) y el audio queda como asset del
// proyecto; el plugin lo levanta con su botón ↻ y lo manda como `audio_url`
// al generar el video. A diferencia de /generar-audio, acá NO se genera nada:
// el audio es el INSUMO del video, y n8n lo convierte a la voz elegida al
// momento de generar. Ver ADR-579.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import DialogueAudioInput, {
  DialogueAudioValue,
} from "../components/DialogueAudioInput";
import { LogoDeMarca } from '../branding';

// Acoplamiento por string con el plugin: `premiere-plugin/src/lib/dialogoAudio.ts`
// filtra los assets de audio del proyecto por este prefijo para encontrar la
// última grabación. Si cambia acá, cambiar allá.
export const DIALOGO_AUDIO_NAME_PREFIX = "Diálogo grabado";

type SubmitState = "idle" | "submitting" | "success" | "error";

const GrabarDialogo: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id") || "";

  const [authChecked, setAuthChecked] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);

  const [audio, setAudio] = useState<DialogueAudioValue | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      if (!session) {
        const next = "/grabar-dialogo" + window.location.search;
        window.location.replace("/auth?next=" + encodeURIComponent(next));
        return;
      }
      if (!projectId) {
        setFatalError("Falta el proyecto. Reabrí desde Atlas.");
        setAuthChecked(true);
        return;
      }
      const uid = session.user.id;
      const { data, error } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", uid)
        .single();
      if (cancelled) return;
      if (error || !data?.id_company) {
        setFatalError("No pudimos resolver tu empresa. Reintentá desde Atlas.");
        setAuthChecked(true);
        return;
      }

      // El navegador y el plugin tienen sesiones separadas: si acá estás
      // logueado con OTRA cuenta, la grabación se guardaría bajo su empresa y
      // el plugin nunca la vería (RLS filtra por empresa) — sin ningún error
      // visible. Por RLS, un proyecto ajeno directamente no se puede leer, así
      // que "no vino fila" cubre tanto el proyecto de otra cuenta como el
      // inexistente. Ver ADR-579.
      const { data: proj } = await supabase
        .from("video_projects")
        .select("id, name")
        .eq("id", projectId)
        .maybeSingle();
      if (cancelled) return;
      if (!proj) {
        setFatalError(
          `En el navegador estás como ${session.user.email}, y este proyecto no es de esa cuenta. ` +
            `Cerrá sesión, entrá con la misma cuenta que usás en Premiere y volvé a tocar "Grabar en la web".`,
        );
        setAuthChecked(true);
        return;
      }

      setUserId(uid);
      setCompanyId(data.id_company as string);
      setUserEmail(session.user.email ?? null);
      setProjectName((proj.name as string) || null);
      setAuthChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const canSubmit =
    !!audio && submitState !== "submitting" && !!userId && !!companyId;

  // El asset ya quedó subido y registrado por DialogueAudioInput (con
  // `name = grabacion_<ts>.webm`). Acá sólo lo renombramos con el prefijo que
  // el plugin busca — no se genera nada.
  const handleSubmit = async () => {
    if (!audio || !userId || !companyId) return;
    setSubmitState("submitting");
    setErrorMessage(null);
    try {
      const stamp = new Date().toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
      const { error } = await supabase
        .from("video_project_assets")
        .update({ name: `${DIALOGO_AUDIO_NAME_PREFIX} ${stamp}` })
        .eq("id", audio.assetId);
      if (error) throw new Error(error.message);
      setSubmitState("success");
    } catch (e) {
      setErrorMessage(
        e instanceof Error
          ? e.message
          : "Hubo un problema técnico. Intentá de nuevo.",
      );
      setSubmitState("error");
    }
  };

  if (!authChecked && !fatalError) {
    return (
      <div className="min-h-[100dvh] bg-surface-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-surface-bg flex flex-col items-center justify-center px-4 py-8 font-sans">
      <LogoDeMarca
        variante="solo"
        className="w-12 h-12 mb-6"
      />
      <div className="w-full max-w-md bg-surface-card rounded-lg shadow-md p-6">
        {fatalError ? (
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 text-error mx-auto mb-2" />
            <p className="text-[14px] text-brand-black/80">{fatalError}</p>
          </div>
        ) : submitState === "success" ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-brand-primary mx-auto mb-3" />
            <h2 className="text-[18px] font-semibold text-brand-black">
              ¡Grabación lista!
            </h2>
            <p className="text-[14px] text-brand-black/70 mt-2 leading-relaxed">
              Volvé a Premiere (Alt+Tab) y tocá ↻ "Traer grabación" para usarla
              en el modo Diálogo.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-[20px] font-semibold text-brand-black mb-1">
              Grabá tu diálogo
            </h1>
            <p className="text-[13px] text-brand-black/60 mb-4">
              Grabá lo que querés que diga la persona. La voz que elegiste en
              Premiere lo va a "imitar", conservando tus pausas y entonación.
            </p>

            {/* Con qué cuenta y en qué proyecto se está grabando. El navegador
                puede tener otra sesión que Premiere; verlo acá evita grabar
                contra el proyecto equivocado. */}
            {(userEmail || projectName) && (
              <div className="mb-5 px-3 py-2 rounded-md bg-neutral-100 border border-neutral-200">
                {projectName && (
                  <p className="text-[12.5px] text-brand-black/70">
                    Proyecto:{" "}
                    <span className="font-medium text-brand-black">
                      {projectName}
                    </span>
                  </p>
                )}
                {userEmail && (
                  <p className="text-[12.5px] text-brand-black/70">
                    Cuenta:{" "}
                    <span className="font-medium text-brand-black">
                      {userEmail}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* `hideUpload` a propósito: esta pantalla es gemela de
                /generar-audio (pedido de homogeneidad). Implica que NO se
                puede mandar un audio pregrabado al modo Diálogo — ni acá ni
                desde el plugin. Decisión registrada en ADR-581, que ajusta el
                ADR-579 (donde se decía que la web cubría ese caso). */}
            <DialogueAudioInput
              value={audio}
              onChange={setAudio}
              projectId={projectId}
              companyId={companyId}
              userId={userId}
              hideUpload
            />

            {errorMessage && (
              <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 my-4">
                <p className="text-[13px] text-red-700 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{errorMessage}</span>
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[14.5px] font-semibold transition-colors mt-4 ${
                canSubmit
                  ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {submitState === "submitting" && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {submitState === "submitting" ? "Guardando…" : "Listo"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GrabarDialogo;
